import { Injectable, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { AuthStore } from '../states/auth.store';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { AppStore } from '../states/app.store';
import { Duration } from 'luxon';
import { AutoBackupService } from './auto-backup.service';
import { SETTINGS } from '@app/domain/ports';
import { IdleControlPort } from '@app/ui/shared';
import { MultiTabSyncService } from './multi-tab-sync.service';
import { SETTINGS_REPOSITORY } from '@app/core/tokens/infrastructure.tokens';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements IdleControlPort {
  private readonly idle = inject(Idle);
  private readonly authStore = inject(AuthStore);
  private readonly appStore = inject(AppStore);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly settings = inject(SETTINGS_REPOSITORY);
  private readonly autoBackupService = inject(AutoBackupService);
  private readonly multiTabSync = inject(MultiTabSyncService);
  private isWatching = false;
  private isPaused = false;
  private isLoggingOut = false;

  private readonly busySources = signal<ReadonlyArray<Signal<boolean>>>([]);
  private readonly isBusy = computed(() => this.busySources().some((s) => s()));

  private readonly autoLockTimeout = computed(() => {
    const autoLockTimeout = this.appStore.autoLockTimeout();
    return Duration.fromISO(autoLockTimeout).as('seconds');
  });

  constructor() {
    this.configureIdle();
    this.setupAutoLockTimeoutWatcher();
    this.setupVisibilityListener();
    this.setupBusySourceWatcher();
  }

  private setupAutoLockTimeoutWatcher(): void {
    effect(() => {
      const timeoutSeconds = this.autoLockTimeout();
      untracked(() => {
        this.idle.setIdle(timeoutSeconds);

        if (this.isWatching && !this.isPaused) {
          this.updateLastActive();
          this.idle.watch();
        }
      });
    });
  }

  private setupBusySourceWatcher(): void {
    effect(() => {
      const busy = this.isBusy();
      untracked(() => {
        if (!this.isWatching) return;
        if (busy && !this.isPaused) {
          this.idle.stop();
          this.isPaused = true;
          console.log('⏸ Idle monitoring paused (busy source active)');
        } else if (!busy && this.isPaused) {
          this.updateLastActive();
          this.idle.watch();
          this.isPaused = false;
          console.log('▶ Idle monitoring resumed');
        }
      });
    });
  }

  /**
   * Register a signal that keeps idle monitoring paused while it is `true`.
   * Typical usage: register a component's "isLoading" / "importing" signal so
   * that long-running operations are not interrupted by the auto-lock timer.
   *
   * Returns a cleanup function — call it in `ngOnDestroy` or `destroyRef.onDestroy()`.
   *
   * @example
   * const release = this.inactivityService.registerBusySource(this.importing);
   * this.destroyRef.onDestroy(release);
   */
  registerBusySource(source: Signal<boolean>): () => void {
    this.busySources.update((sources) => [...sources, source]);
    return () => {
      this.busySources.update((sources) => sources.filter((s) => s !== source));
    };
  }

  private configureIdle(): void {
    // Set idle timeout to 15 minutes (900 seconds)
    this.idle.setIdle(this.autoLockTimeout());

    // No additional timeout period - logout immediately after idle period
    this.idle.setTimeout(5);

    // Set default interrupts (mousemove, keydown, DOMMouseScroll, mousewheel, mousedown, touchstart, touchmove, scroll, keyup)
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Subscribe to timeout event
    this.idle.onTimeout.subscribe(() => {
      this.handleTimeout().catch((error) => {
        console.error('Fatal error during idle timeout execution:', error);
        // If everything else fails, force-drop the user to safety
        window.location.assign('/login');
      });
    });

    // Subscribe to idle start event to update last active timestamp
    this.idle.onIdleStart.subscribe(() => {
      this.updateLastActive();
    });

    // Update last active on any interrupt (user activity)
    this.idle.onInterrupt.subscribe(() => {
      this.updateLastActive();
    });
  }

  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkBackgroundTimeout();
      } else {
        // Store timestamp when app goes to background
        this.updateLastActive();
      }
    });
  }

  private updateLastActive(): void {
    this.settings.set(SETTINGS.LAST_ACTIVITY_TIMESTAMP, Date.now());
  }

  private checkBackgroundTimeout(): void {
    if (this.isBusy()) {
      this.updateLastActive();
      return;
    }
    const lastActive = this.settings.get<number>(SETTINGS.LAST_ACTIVITY_TIMESTAMP, 0);
    if (!lastActive) {
      this.updateLastActive();
      return;
    }

    const elapsed = Date.now() - lastActive;
    const timeoutMs = this.autoLockTimeout() * 1000;

    // If more than 15 minutes have passed while app was in background, logout immediately
    if (elapsed >= timeoutMs) {
      this.handleTimeout().catch((error) => {
        console.error('Fatal error during checkBackgroundTimeout execution:', error);
      });
    } else {
      // App returned before timeout - continue watching from current point
      this.updateLastActive();
    }
  }

  private async handleTimeout(): Promise<void> {
    if (!this.isWatching) return;
    if (this.isBusy()) return;
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    this.stop();
    console.log('Idle service forces app to logout');

    try {
      // Mark the upcoming lock as inactivity-triggered so other tabs are NOT
      // forced to lock — each tab manages its own idle timer independently.
      this.multiTabSync.setPendingLockReason('inactivity');

      // Try to create auto-backup before lock (if changes exist)
      try {
        await this.autoBackupService.tryCreateBackupBeforeExit();
      } catch (error) {
        // Non-fatal: backup-before-lock is best-effort. Lock proceeds regardless
        // to prevent data exposure; failure is not surfaced to the user here.
        console.warn('Failed to create backup before lock:', error);
      }

      // Close all open PrimeNG dialogs before logout
      try {
        Array.from(this.dialogService.dialogComponentRefMap.entries()).forEach(([dialogRef, dialogComponentRef]) => {
          if (typeof dialogRef?.destroy === 'function') {
            dialogRef.destroy();
            return;
          }

          if (typeof dialogComponentRef?.destroy === 'function') {
            dialogComponentRef.destroy();
          }
        });
      } catch (error) {
        console.error('Dialog close encountered an error', error);
      }

      try {
        await this.authStore.logout();
      } catch (error) {
        console.error('AuthStore logout encountered an error', error);
      }

      // Store current URL for return after login
      const returnUrl = this.router.routerState.snapshot.url; // this.router.url;
      const navigated = await this.router.navigate(['/login'], { queryParams: { returnUrl } });
      if (!navigated) {
        window.location.assign('/login');
      }
    } finally {
      this.isLoggingOut = false;
    }
  }

  /**
   * Start watching for inactivity
   */
  start(): void {
    if (!this.isWatching) {
      this.updateLastActive();
      this.isWatching = true;
      if (this.isBusy()) {
        this.idle.stop();
        this.isPaused = true;
        console.log('⏸ Idle monitoring started paused (busy source active)');
        return;
      }
      this.isPaused = false;
      this.idle.watch();
      console.log('✓ Iddle monitoring started');
    }
  }

  /**
   * Stop watching for inactivity
   */
  stop(): void {
    if (this.isWatching) {
      this.idle.stop();
      this.isWatching = false;
      this.isPaused = false;
      this.settings.remove(SETTINGS.LAST_ACTIVITY_TIMESTAMP);
      console.log('Iddle monitoring finished');
    }
  }

  /**
   * Reset the idle timer (useful after manual actions that should reset timeout)
   */
  reset(): void {
    if (this.isWatching) {
      this.updateLastActive();
    }
  }
}
